import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Copy, Navigation, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ADDRESS = "RL 38, Gangaram Vatika, Tilak Nagar, New Delhi — 110018";
const MAPS_URL =
  "https://maps.google.com/?q=RL+38+Gangaram+Vatika+Tilak+Nagar+New+Delhi+110018";

const cards = [
  {
    testid: "event-date-card",
    icon: Calendar,
    title: "The Date",
    line1: "Friday, 20th November 2026",
    line2: "Two nights before the Wedding",
  },
  {
    testid: "event-time-card",
    icon: Clock,
    title: "The Time",
    line1: "7:00 PM Onwards",
    line2: "Dinner, drinks & dance through the night",
  },
];

export const Details = () => {
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      toast.success("Address copied to clipboard");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = ADDRESS;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Address copied to clipboard");
      } catch {
        toast.error("Could not copy — please long-press the address");
      }
    }
  };

  return (
    <section id="details" data-testid="event-details-section" className="relative py-20 sm:py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-amber-400">
            When &amp; Where
          </p>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-amber-200 mt-4">
            Evening Details
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {cards.map(({ testid, icon: Icon, title, line1, line2 }, i) => (
            <motion.div
              key={testid}
              data-testid={testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="card-glass rounded-2xl p-7 text-center"
            >
              <Icon className="w-7 h-7 text-amber-400 mx-auto" />
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-4">
                {title}
              </p>
              <p className="font-serif-display text-xl sm:text-2xl text-amber-50 mt-2">{line1}</p>
              <p className="text-sm text-zinc-400 mt-1">{line2}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          data-testid="venue-address-card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card-glass rounded-2xl p-7 sm:p-9 mt-5 text-center"
        >
          <MapPin className="w-7 h-7 text-amber-400 mx-auto" />
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-4">
            The Venue · Our Residence
          </p>
          <p className="font-serif-display text-xl sm:text-2xl text-amber-50 mt-2">{ADDRESS}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <a
              data-testid="venue-google-maps-button"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
            >
              <Navigation className="w-4 h-4" /> Get Directions
            </a>
            <button
              data-testid="venue-copy-address-button"
              onClick={copyAddress}
              className="btn-outline-gold rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
            >
              <Copy className="w-4 h-4" /> Copy Address
            </button>
          </div>
        </motion.div>

        <motion.p
          data-testid="dress-code-note"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="text-center text-sm text-zinc-300 mt-10 flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4 text-amber-400" />
          Dress Code — <span className="text-amber-300 font-medium">Cocktail Glam</span>
        </motion.p>
      </div>
    </section>
  );
};
